import PropTypes from 'prop-types';
import React, { Component } from 'react';
import StaffPicks from 'components/Badges/StaffPicks/StaffPicks';
import { getThumbnailByWidth } from '../../../helpers/HelperFunctions';
import styles from './VideoPlayer.module.scss';

export default class VideoPlayer extends Component {
  static propTypes = {
    project: PropTypes.object,
    staffPicks: PropTypes.bool,
    user: PropTypes.object,
    onLoad: PropTypes.func,
  };

  constructor() {
    super();

    this.state = {
      player: null,
      loaded: false,
      loading: true,
      playing: false,
      playerElement: null,
    };

    this.destroyPlayer = this.destroyPlayer.bind(this);
    this.onPlaying = this.onPlaying.bind(this);
    this.onPause = this.onPause.bind(this);
    this.onEnded = this.onEnded.bind(this);
  }

  componentDidMount() {
    require('hls.js');
    require('mediaelement');
    require('mediaelement/build/mediaelementplayer.css');
    require('mediaelement/build/mediaelement-flash-video.swf');
    require('mediaelement/build/mediaelement-flash-video-hls.swf');
    require('mediaelement-plugins/dist/quality/quality.min.css');
    require('mediaelement-plugins/dist/quality/quality.min.js');

    this.initiatePlayer('video-player');
  }

  UNSAFE_componentWillUpdate(nextProps) {
    if (nextProps.project && nextProps.project.id !== this.props.project.id) {
      this.destroyPlayer(true);
    }
  }

  componentWillUnmount() {
    this.destroyPlayer();
  }

  onPlaying() {
    this.setState({ playing: true });
  }

  onPause() {
    this.setState({ playing: false });
  }

  onEnded() {
    this.setState({ playing: false });
  }

  initiatePlayer(element) {
    const options = {
      // Read the Notes below for more explanation about how to set up the path for shims
      pluginPath: '../../../../node_modules/mediaelement-plugins',
      success: (player) => this.success(player),
      error: () => this.error(),
      features: [
        'playpause',
        'current',
        'progress',
        'duration',
        'tracks',
        'quality',
        'volume',
        'fullscreen',
      ],
      stretching: 'responsive',
      hideVideoControlsOnLoad: true,
      hideVideoControlOnPause: true,
      videoHeight: document.getElementById(element).clientWidth / (16 / 9),
      defaultVideoHeight:
        document.getElementById(element).clientWidth / (16 / 9),
      videoWidth: document.getElementById(element).clientWidth,
      defaultVideoWidth: document.getElementById(element).clientWidth,
      iPadUseNativeControls: false,
      iPhoneUseNativeControls: false,
      AndroidUseNativeControls: false,
      videoVolume: 'vertical',
    };

    const { MediaElementPlayer } = global;

    if (!MediaElementPlayer) {
      return;
    }

    this.setState({
      player: new MediaElementPlayer(element, options),
    });
  }

  destroyPlayer(reinit) {
    if (this.state.playerElement) {
      this.state.playerElement.removeEventListener('playing', this.onPlaying);
      this.state.playerElement.removeEventListener('pause', this.onPause);
      this.state.playerElement.removeEventListener('ended', this.onEnded);
    }

    if (this.state.player) {
      try {
        if (this.state.playing) {
          this.state.player.pause();
        }

        this.state.player.remove();
      } catch (err) {
        // ignore
      }

      this.setState(
        {
          player: null,
          loaded: false,
          loading: true,
          playing: false,
          playerElement: null,
        },
        () => {
          if (reinit) {
            this.initiatePlayer('video-player');
          }
        }
      );
    }
  }

  success(player) {
    // Your action when media was successfully loaded
    this.setState({ loaded: true, loading: false, playerElement: player });

    player.addEventListener('playing', this.onPlaying, false);
    player.addEventListener('pause', this.onPause, false);
    player.addEventListener('ended', this.onEnded, false);

    if (this.props.onLoad) {
      this.props.onLoad();
    }
  }

  error() {
    // Your action when media had an error loading
    this.setState({ loaded: false, loading: false });
  }

  supportsHls() {
    if (__CLIENT__) {
      const element = document.createElement('video');

      if (element) {
        let canPlay = false;

        // canPlayType does not work on Opera Mini
        if (typeof element.canPlayType === 'function') {
          canPlay = element.canPlayType('application/x-mpegurl');
        }

        return canPlay;
      }
    }

    return false;
  }

  render() {
    const { video } = this.props.project.stream;
    const { thumbnails } = this.props.project;
    const { project, user } = this.props;

    const sources = [];

    // Problems on Android when navigating to and from projects means the HLS version is attempted on mobile
    // Which doesn't work for Android
    //
    // if (this.supportsHls()) {
    //   sources.push({
    //     src: project.stream.hls.website.video,
    //     type: 'application/x-mpegurl',
    //   });
    // }

    Object.keys(video)
      .reverse()
      .forEach(function (height) {
        Object.keys(video[height])
          .reverse()
          .forEach(function (bitrate) {
            Object.keys(video[height][bitrate]).forEach(function (format) {
              if (format != 'application/x-mpegurl') {
                sources.push({
                  src: video[height][bitrate][format],
                  type: format === 'mp4' ? 'video/mp4' : '',
                  quality: height,
                  bitrate: bitrate,
                });
              }
            });
          });
      });

    const poster = getThumbnailByWidth(thumbnails.images, 500);

    const sourceTags = sources.map(
      (source) =>
        `<source src="${source.src}" type="${source.type}" title="${
          source.quality ? source.quality : 'auto'
        }" data-quality="${source.quality ? source.quality : 'auto'}" />`
    );

    const mediaBody = `${sourceTags.join('\n')}`;

    const mediaHtml = `<video id="video-player" controls width="100%" poster="${poster}" style="background-color: ${thumbnails.color}">${mediaBody}</video>`;

    const durationHours =
      project && project.stream ? project.stream.duration / 3600 : 0;
    const durationRemainder = project.stream.duration % 3600;
    const duration = {
      hours: Math.floor(durationHours),
      minutes: Math.floor(durationRemainder / 60),
      seconds: Math.floor(durationRemainder % 60),
    };

    return (
      <div>
        <span
          itemProp="duration"
          content={`T${duration.hours}H${duration.minutes}M${duration.seconds}S`}
        />
        <span
          itemProp="embedUrl"
          content={
            project && project.links && project.links.widget
              ? project.links.widget
              : ''
          }
        />
        <span
          itemProp="thumbnail"
          content={
            project && project.thumbnails && project.thumbnails.original
              ? project.thumbnails.original
              : ''
          }
        />
        <span
          itemProp="datePublished"
          content={project && project.created_at ? project.created_at : ''}
        />
        <span
          itemProp="author"
          content={user && user.displayname ? user.displayname : ''}
        />
        <div dangerouslySetInnerHTML={{ __html: mediaHtml }}></div>
        {!this.state.loading && !this.state.playing && this.props.staffPicks ? (
          <StaffPicks
            project={this.props.project}
            className={styles.staffPicksBadge}
          />
        ) : (
          <noscript />
        )}
      </div>
    );
  }
}
